import React from 'react';
import { Card } from 'react-bootstrap';
import { Award } from 'lucide-react';
import { getBadgeTooltip } from './performanceBadgeCopy';

export { getBadgeTooltip, getFriendlyBadgeDescription } from './performanceBadgeCopy';

export function PerformanceBadgePills({
  badges = [],
  className = '',
  emptyPlaceholder = null,
}) {
  if (!badges.length) {
    return emptyPlaceholder;
  }
  return (
    <div className={`performance-team-badges ${className}`.trim()}>
      {badges.map((b) => (
        <span
          key={b.key}
          className={`performance-team-badge-pill badge-tier-${b.tier}`}
          title={getBadgeTooltip(b)}
        >
          <Award size={11} aria-hidden />
          <span className="performance-team-badge-pill__label">{b.name}</span>
        </span>
      ))}
    </div>
  );
}

export const PERIOD_KEYS = ['previous_month', 'current_month_mtd', 'year_till_last_month'];

/** Overview duration selector — maps to API period keys. */
export const OVERVIEW_PERIOD_OPTIONS = [
  { key: 'current_month_mtd', shortLabel: 'This month' },
  { key: 'previous_month', shortLabel: 'Last month' },
  { key: 'year_till_last_month', shortLabel: 'This year' },
];

export const DEFAULT_OVERVIEW_PERIOD_KEY = 'current_month_mtd';

export function scoreColorClass(color) {
  return `score-${color || 'grey'}`;
}

/** Highlight class for the logged-in user's table row (score-band tinted). */
export function getMeRowClassName(row, currentUserId, scoreColor) {
  if (String(row.user_id) !== String(currentUserId)) return '';
  return `performance-top-five-row--me ${scoreColorClass(scoreColor)}`;
}

export function ScoreLevelBadge({ color }) {
  if (!color?.level) return null;
  return (
    <span className={`score-level-badge ${scoreColorClass(color.color)}`}>
      {color.level.replace(/_/g, ' ')}
    </span>
  );
}

export function ScoreCircle({ score, color, size = 'md' }) {
  const display = score == null ? '—' : score;
  return (
    <div className={`score-circle performance-score-ring ${size === 'sm' ? 'sm' : ''} ${scoreColorClass(color?.color || color)}`}>
      {display}
    </div>
  );
}

export function PeriodScoreCard({ periodMeta, scoreData }) {
  if (periodMeta?.empty) {
    return (
      <Card className="user-list-card period-score-card">
        <Card.Body className="text-center text-muted">
          <div className="period-score-label">{periodMeta.label || periodMeta.key?.replace(/_/g, ' ')}</div>
          <ScoreCircle score={null} color="grey" />
          <small>{periodMeta.empty_reason || 'Not available'}</small>
        </Card.Body>
      </Card>
    );
  }

  const score = scoreData?.performance_score;
  const color = scoreData?.color;
  return (
    <Card className="user-list-card period-score-card">
      <Card.Body className="text-center">
        <div className="period-score-label">{periodMeta?.label || 'Period'}</div>
        <ScoreCircle score={score} color={color} />
        <ScoreLevelBadge color={color} />
      </Card.Body>
    </Card>
  );
}

/** Top N corporate scores for a period bucket, highest first. */
export function getCorporateTopScores(teamData, periodKey, limit = 5) {
  return (teamData?.users || [])
    .map((u) => {
      const entry = u.scores?.[periodKey];
      const score = entry?.performance_score;
      if (score == null) return null;
      return {
        user_id: u.user_id,
        full_name: u.full_name,
        department: u.department,
        performance_score: score,
        color: entry.color,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.performance_score - a.performance_score)
    .slice(0, limit);
}

/** @deprecated use getCorporateTopScores */
export function findCorporateTopScore(teamData, periodKey = DEFAULT_OVERVIEW_PERIOD_KEY) {
  const [top] = getCorporateTopScores(teamData, periodKey, 1);
  return top ?? null;
}
